import { Component, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { capitalize, clone } from 'lodash';
import { BsModalService, BsModalRef } from 'ngx-bootstrap/modal';
import { NgForm } from '../../../../../node_modules/@angular/forms';
import { BaseModalComponent } from '../../../models/base-modal-component';
import { DrEntityAttribute } from '../../../models/dr-entity-attribute';
import { FileSizeUnit, HelpIndex, TextOperations, TransformerType } from '../../../models/enums';
import { LoadFileMetadataProfileCompositeFieldMember } from '../../../models/field-mapping';
import { Selectable } from '../../../models/selectable';
import { CommonService } from '../../../services/common/common.service';

@Component({
    standalone: false,
    selector: 'res-transformers-modal',
    templateUrl: './transformers-modal.component.html',
    styleUrls: ['./transformers-modal.component.scss'],
    encapsulation: ViewEncapsulation.None
})

export class TransformersModalComponent extends BaseModalComponent implements OnInit {
    @ViewChild('transformersContentForm', { static: true }) transformersContentForm: NgForm;

    modalClass = 'transformers-modal';
    modalTitle: string;
    modalAutomationId: string;
    modalContainerAutomationId: string;
    modalHelpIndex: HelpIndex;

    transformerType: TransformerType;

    // number transformer
    fileSizes = FileSizeUnit;
    selectedFileSize = 'GB';

    // text transformer
    selectedTextOperation: string = TextOperations.concat;
    textOperations = TextOperations;
    concatDelimiters = [';', 'Space'];
    selectedDelimiter = this.concatDelimiters[0];
    selectedConcatField: string;
    trimChars: string;
    inputChars: string;
    replaceField: string;
    withField: string;

    // date transformer
    dateFormats = ['MM/dd/yyyy', 'MM/dd/yy', 'yy/MM/dd', 'yyyy/MM/dd', 'dd/MMM/yy'];
    timeFormats = ['HH:mm:ss', 'hh:mm:ss a', 'h:mm:ss a'];
    _isDateTimeOneField: boolean;
    set isDateTimeOneField(value: boolean) {
        this._isDateTimeOneField = value;
    }
    get isDateTimeOneField(): boolean {
        return this._isDateTimeOneField;
    }
    selectedDateFormat = this.dateFormats[0];
    selectedTimeFormat = this.timeFormats[0];
    isDateFirst = true;
    isTimeFirst = false;
    dateFieldList: Array<Selectable>;
    selectedDateField: string;

    fieldsSelectAreaClass: string;
    fieldList: Array<Selectable>;
    selectedField: string;

    constructor(
        private modalService: BsModalService,
        private modalRef: BsModalRef,
        private commonService: CommonService
    ) {
        super(modalService, modalRef);
    }

    ngOnInit(): void {
        const { fields, selectedLoadFileField } = this.modalData;
        if (selectedLoadFileField) {
            const modalType = this.getEnumKeyByValue(TransformerType, selectedLoadFileField.fieldTransformerType);

            this.transformerType = selectedLoadFileField.fieldTransformerType;
            this.modalTitle = modalType !== 'date' ? `${capitalize(modalType)} Transformer` : 'Date / Time Transformer';
            this.modalAutomationId = `${modalType}-transformer-modal`;
            this.modalContainerAutomationId = `${modalType}-transformer`;
            this.fieldsSelectAreaClass = `${modalType}-transformer-fields`;
            this.modalClass += ` ${modalType}-transformer`;

            if (this.transformerType === TransformerType.date) {
                this.modalHelpIndex = HelpIndex.load_file_date_transformer_dlg;
                this.dateFieldList = clone(fields);
                if (this.dateFieldList && this.dateFieldList.length > 0) {
                    this.selectedDateField = selectedLoadFileField.externalName;
                }
            } else {
                this.modalHelpIndex = (this.transformerType === TransformerType.number) ? HelpIndex.load_file_number_transformer_dlg : HelpIndex.load_file_text_transformer_dlg;
            }
            this.fieldList = clone(fields);
            if (this.fieldList && this.fieldList.length > 0) {
                if (this.transformerType === TransformerType.date) {
                    this.fieldList.unshift(new Selectable({ externalName: '' }));
                    this.selectedField = '';
                    this.selectedDateField = selectedLoadFileField.externalName;
                } else {
                    this.selectedField = selectedLoadFileField.externalName;
                }
            }
            if (selectedLoadFileField.transformerAttributes) {
                this.setAttributesToControls(selectedLoadFileField.transformerAttributes, selectedLoadFileField.compositeFields);
            }
        }
    }

    // where key can be text/date/number
    getModalTypeByKeyName(key: string): boolean {
        return this.transformerType === TransformerType[key];
    }

    isPrimaryButtonEnabled(): boolean {
        if (this.getModalTypeByKeyName('date')) {
            const checkForDelim = this.isDateTimeOneField ? this.selectedDelimiter.trim() !== '' : true;
            return this.transformersContentForm.valid && this.selectedDateFormat.trim() !== '' && this.selectedTimeFormat.trim() !== '' && checkForDelim;
        }
        if (this.getModalTypeByKeyName('text') && this.selectedTextOperation === TextOperations.concat) {
            return this.transformersContentForm.valid && this.selectedDelimiter.trim() !== '';
        }
        return this.transformersContentForm.valid;
    }

    isSufixPrefixOperation(): boolean {
        return this.selectedTextOperation === TextOperations.suffix || this.selectedTextOperation === TextOperations.prefix;
    }

    isTrimOperation(): boolean {
        return this.selectedTextOperation === TextOperations.ltrim || this.selectedTextOperation === TextOperations.rtrim;
    }

    isLowerUperCaseOperation(): boolean {
        return this.selectedTextOperation === TextOperations.lowercase || this.selectedTextOperation === TextOperations.uppercase;
    }

    onDelimiterChanged = (item: any): void => {
        this.selectedDelimiter = item;
    }

    onSelectedDateChanged = (item: any): void => {
        this.selectedDateFormat = item;
    }

    onSelectedTimeChanged = (item: any): void => {
        this.selectedTimeFormat = item;
    }

    setAttributesToControls(attribs: Array<DrEntityAttribute>, compositeFields: Array<LoadFileMetadataProfileCompositeFieldMember>): void {
        if (attribs.length > 0) {
            if (this.transformerType === TransformerType.text) {
                const operationName = attribs[0].name.toLowerCase();
                const txtOperation = TextOperations[operationName];
                if (txtOperation) {
                    this.selectedTextOperation = txtOperation;
                    if (this.isSufixPrefixOperation()) {
                        this.inputChars = attribs[0].value;
                    } else if (this.isTrimOperation()) {
                        this.trimChars = attribs[0].value;
                    } else if (this.selectedTextOperation === TextOperations.replace) {
                        const replaceValues = attribs[0].value.split(',');
                        if (replaceValues.length === 2) {
                            this.replaceField = replaceValues[0];
                            this.withField = replaceValues[1];
                        }
                    } else if (this.selectedTextOperation === TextOperations.concat) {
                        const delimAttrib = attribs.find(a => a.name === 'delim');
                        const concatAttrib = attribs.find(a => a.name === 'CONCAT');
                        const delim = delimAttrib.value === 'Space' ? ' ' : delimAttrib.value;
                        if (delimAttrib) {
                            this.selectedDelimiter = delimAttrib.value;
                            if (concatAttrib && concatAttrib.value) {
                                const concatFields = concatAttrib.value.split(delim);
                                if (concatFields && concatFields.length === 2) {
                                    this.selectedField = concatFields[0];
                                    this.selectedConcatField = concatFields[1];
                                }
                            }
                        }
                    }
                }
            } else if (this.transformerType === TransformerType.number) {
                if (attribs[0].name === 'CONVERT_FILE_SIZE') {
                    this.selectedFileSize = this.getEnumKeyByValue(FileSizeUnit, attribs[0].value);
                }
            } else if (this.transformerType === TransformerType.date) {
                const dateFormat = attribs.find(a => a.name === 'dateformat');
                const timeFormat = attribs.find(a => a.name === 'timeformat');
                if (dateFormat) {
                    this.selectedDateFormat = dateFormat.value;
                }
                if (timeFormat) {
                    this.selectedTimeFormat = timeFormat.value;
                }
                if (compositeFields && compositeFields.length === 2) {
                    this.isDateFirst = compositeFields[0].fieldType === 'DATE';
                    this.isTimeFirst = compositeFields[1].fieldType !== 'TIME';
                    this.selectedDateField = compositeFields[0].fieldName;
                    this.selectedField = compositeFields[1].fieldName;
                } else {
                    this.isDateTimeOneField = true;
                    const delimAttrib = attribs.find(a => a.name === 'delimiter');
                    if (delimAttrib) {
                        this.selectedDelimiter = delimAttrib.value === ' ' ? this.concatDelimiters[1] : delimAttrib.value;
                    }
                }
            }
        }
    }

    ok(): void {
        let compositeFields = null;
        let transformerAttribs = null;
        if (this.getModalTypeByKeyName('number')) {
            transformerAttribs = new Array<DrEntityAttribute>({ name: 'CONVERT_FILE_SIZE', value: FileSizeUnit[this.selectedFileSize] });
        } else if (this.getModalTypeByKeyName('text')) {
            const operationName = this.getEnumKeyByValue(TextOperations, this.selectedTextOperation);
            if (operationName) {
                transformerAttribs = new Array<DrEntityAttribute>({ name: operationName.toUpperCase(), value: '' });
                if (this.isTrimOperation()) {
                    transformerAttribs[0].value = this.trimChars;
                } else if (this.isSufixPrefixOperation()) {
                    transformerAttribs[0].value = this.inputChars;
                } else if (this.selectedTextOperation === TextOperations.concat) {
                    if (this.selectedConcatField === this.selectedField) {
                        this.commonService.openWarningModal('The selected load file fields must be different.', 'Warning');
                        return;
                    } else {
                        transformerAttribs[0].value = this.selectedField + (this.selectedDelimiter === 'Space' ? ' ' : this.selectedDelimiter) + this.selectedConcatField;
                        this.selectedField = this.selectedField + ';' + this.selectedConcatField;
                        transformerAttribs.push({ name: 'delim', value: this.selectedDelimiter });
                    }
                } else if (this.selectedTextOperation === TextOperations.replace) {
                    transformerAttribs[0].value = `${this.replaceField},${this.withField}`;
                }
            }
        } else if (this.getModalTypeByKeyName('date')) {
            if (this.isDateTimeOneField) {
                transformerAttribs = [
                    { name: 'dateformat', value: this.selectedDateFormat },
                    { name: 'timeformat', value: this.selectedTimeFormat },
                    { name: 'delimiter', value: this.selectedDelimiter === 'Space' ? ' ' : this.selectedDelimiter }
                ];
                this.selectedField = this.selectedDateField;
            } else {
                compositeFields = [
                    { fieldName: this.selectedDateField, fieldType: this.isDateFirst ? 'DATE' : 'TIME' },
                    { fieldName: this.selectedField, fieldType: !this.isTimeFirst ? 'TIME' : 'DATE' }
                ];
                transformerAttribs = [
                    { name: 'dateformat', value: this.selectedDateFormat },
                    { name: 'timeformat', value: this.selectedTimeFormat }
                ];
                this.selectedField = this.selectedDateField + ';' + this.selectedField;
            }
        }
        this.closeModal(true, { transformerAttribs: transformerAttribs, compositeFields: compositeFields, selectedField: this.selectedField, fieldTransformerType: this.transformerType });
    }

    getEnumKeyByValue(T: any, value: string): string {
        const enumNames = Object.keys(T);
        let keyName = null;
        enumNames.forEach(name => { if (T[name] === value) { keyName = name; } });
        return keyName;
    }

    isDateFirstChanged(): void {
        this.isTimeFirst = !this.isDateFirst;
    }

    isTimeFirstChanged(): void {
        this.isDateFirst = !this.isTimeFirst;
    }

    onFocusChanged = (item: any): void => {
        return item;
    }

}
